import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, CalendarDays, TrendingUp, Users, Car, ChevronRight, Clock } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fmtEUR } from "../../../lib/api";
import { shiftDay, STATUS_TONE } from "./util";

const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const short = (iso) => `${Number(iso.slice(8, 10))} ${MESI[Number(iso.slice(5, 7)) - 1]}`;
const longDate = (iso) => `${iso.slice(8, 10)} ${MESI[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
const live = (b) => b.status !== "Cancellata";

export function DashHero({ tools, day, setDay }) {
  return (
    <section className="relative rounded-2xl border border-[#D4A72C]/25 min-h-[180px] md:min-h-[210px] z-20" data-testid="mb-hero">
      <div className="absolute inset-0 overflow-hidden rounded-2xl">
        <img src="/img/rome-hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-[center_40%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 to-transparent" />
      </div>
      <div className="relative z-10 p-5 md:p-7 flex flex-col gap-5">
        <div className="hidden lg:flex items-center gap-3 justify-end" data-testid="mb-hero-tools">{tools}</div>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 rounded-full border border-[#D4A72C]/50 bg-black/50 flex items-center justify-center shadow-[0_0_24px_-6px_rgba(212,167,44,0.6)]"><BarChart3 size={24} className="text-[#E9C46A]" /></span>
            <div>
              <h1 className="font-display text-4xl sm:text-5xl text-white font-semibold leading-none" data-testid="mb-greeting">Dashboard</h1>
              <p className="text-slate-300 mt-2 text-sm md:text-base">Panoramica attività di Striano NCC</p>
            </div>
          </div>
          <label className="relative inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-[#D4A72C]/45 bg-black/60 text-white cursor-pointer self-start sm:self-auto" data-testid="mb-date-picker">
            <CalendarDays size={18} className="text-[#E9C46A]" />
            <span className="font-mono-num text-sm" data-testid="mb-date-label">{longDate(day)}</span>
            <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} data-testid="mb-date-input"
              className="absolute inset-0 opacity-0 cursor-pointer [color-scheme:dark]" />
          </label>
        </div>
      </div>
    </section>
  );
}

const Kpi = ({ icon: Icon, label, value, sub, to, testid, ring }) => {
  const nav = useNavigate();
  return (
    <button type="button" onClick={() => nav(to)} data-testid={testid} className="mb-card mb-lift p-4 flex items-center gap-3 text-left w-full min-w-0">
      <span className="hidden sm:flex w-14 h-14 rounded-xl border border-[#D4A72C]/35 bg-black/40 items-center justify-center shrink-0"><Icon size={24} className="text-[#E9C46A]" strokeWidth={1.6} /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[10.5px] uppercase tracking-[0.18em] text-slate-300 leading-tight">{label}</span>
        <span className="block text-2xl sm:text-[30px] leading-tight font-semibold text-white font-mono-num truncate" data-testid={`${testid}-value`}>{value}</span>
        <span className="block text-[11px] text-slate-400 truncate" data-testid={`${testid}-sub`}>{sub}</span>
      </span>
      {ring != null ? (
        <span className="relative w-14 h-14 shrink-0">
          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90"><circle cx="18" cy="18" r="15" fill="none" stroke="#262218" strokeWidth="2.5" /><circle cx="18" cy="18" r="15" fill="none" stroke="#E9C46A" strokeWidth="2.5" strokeDasharray={`${ring * 0.94} 100`} strokeLinecap="round" /></svg>
          <span className="absolute inset-0 flex items-center justify-center text-[11px] text-white font-mono-num">{ring}%</span>
        </span>
      ) : <ChevronRight size={18} className="text-[#E9C46A]/80 shrink-0" />}
    </button>
  );
};

export function DashKpis({ bookings, customers, vehicles, inService, day, isToday }) {
  const k = useMemo(() => {
    const dayB = bookings.filter((b) => b.date === day && live(b));
    const month = bookings.filter((b) => (b.date || "").startsWith(day.slice(0, 7)) && live(b));
    return {
      count: dayB.length, total: bookings.filter((b) => b.date === day).length,
      rev: dayB.reduce((s, b) => s + Number(b.price || 0), 0), revMonth: month.reduce((s, b) => s + Number(b.price || 0), 0),
      clients: new Set(dayB.map((b) => b.customer_id).filter(Boolean)).size,
    };
  }, [bookings, day]);
  const pct = vehicles.length ? Math.round((inService / vehicles.length) * 100) : 0;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mb-kpis">
      <Kpi icon={CalendarDays} label={isToday ? "Prenotazioni oggi" : "Prenotazioni del giorno"} value={k.count} sub={`Totali: ${k.total}`} to="/prenotazioni" testid="mb-kpi-services" />
      <Kpi icon={TrendingUp} label={isToday ? "Fatturato oggi" : "Fatturato del giorno"} value={fmtEUR(k.rev)} sub={`${fmtEUR(k.revMonth)} mese`} to="/conteggi" testid="mb-kpi-revenue" />
      <Kpi icon={Users} label="Clienti serviti" value={k.clients} sub={`${customers.length} in anagrafica`} to="/clienti" testid="mb-kpi-clients" />
      <Kpi icon={Car} label="Veicoli in servizio" value={`${inService}/${vehicles.length}`} sub="In corso ora" to="/flotta" testid="mb-kpi-vehicles" ring={pct} />
    </div>
  );
}

function trend(bookings, day, mode) {
  const sum = (pred) => bookings.filter((b) => live(b) && b.date && pred(b.date)).reduce((s, b) => s + Number(b.price || 0), 0);
  if (mode === "giorno") return Array.from({ length: 7 }, (_, i) => { const d = shiftDay(day, i - 6); return { label: short(d), value: sum((x) => x === d) }; });
  if (mode === "settimana") {
    const dow = (new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7;
    const mon = shiftDay(day, -dow);
    return Array.from({ length: 8 }, (_, i) => { const s = shiftDay(mon, (i - 7) * 7), e = shiftDay(s, 6); return { label: short(s), value: sum((x) => x >= s && x <= e) }; });
  }
  const [y, m] = day.split("-").map(Number);
  return Array.from({ length: 6 }, (_, i) => { const d = new Date(Date.UTC(y, m - 1 - (5 - i), 1)); const ym = d.toISOString().slice(0, 7); return { label: `${MESI[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`, value: sum((x) => x.startsWith(ym)) }; });
}

export function RevenueTrend({ bookings, day }) {
  const [mode, setMode] = useState("giorno");
  const data = useMemo(() => trend(bookings, day, mode), [bookings, day, mode]);
  return (
    <section className="mb-card p-4 md:p-5 min-w-0" data-testid="mb-revenue-trend">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="flex items-center gap-2 text-white text-base md:text-lg font-semibold"><BarChart3 size={18} className="text-[#E9C46A]" /> Andamento Fatturato</h2>
        <div className="flex rounded-full border border-[#D4A72C]/30 p-0.5 bg-black/40">
          {["giorno", "settimana", "mese"].map((k) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`mb-trend-${k}`}
              className={`px-3.5 h-8 rounded-full text-xs capitalize transition-colors ${mode === k ? "btn-gold-primary font-bold" : "text-slate-300 hover:text-white"}`}>{k}</button>
          ))}
        </div>
      </div>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
            <defs><linearGradient id="mbGold" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E9C46A" stopOpacity={0.55} /><stop offset="100%" stopColor="#E9C46A" stopOpacity={0.02} /></linearGradient></defs>
            <CartesianGrid stroke="rgba(212,167,44,0.08)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#A8A39A", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#A8A39A", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v} €`} width={64} />
            <Tooltip contentStyle={{ background: "#0B0A08", border: "1px solid rgba(212,167,44,0.4)", borderRadius: 10, color: "#fff" }} formatter={(v) => [fmtEUR(v), "Fatturato"]} />
            <Area type="monotone" dataKey="value" stroke="#E9C46A" strokeWidth={2.5} fill="url(#mbGold)" dot={{ r: 3.5, fill: "#F6E3A8", stroke: "#E9C46A" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

export function TodayList({ bookings, day, vehicleName }) {
  const nav = useNavigate();
  const rows = bookings.filter((b) => b.date === day).sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")));
  return (
    <section className="mb-card p-4 md:p-5 min-w-0 flex flex-col" data-testid="mb-today-list">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 text-white text-base md:text-lg font-semibold"><CalendarDays size={18} className="text-[#E9C46A]" /> Servizi del giorno</h2>
        <button type="button" onClick={() => nav(`/prenotazioni?date=${day}`)} data-testid="mb-daylist-all" className="px-3 h-8 rounded-full border border-[#D4A72C]/45 text-[#F6E3A8] text-xs hover:bg-[#D4A72C]/10">Vedi tutti</button>
      </div>
      <div className="space-y-1.5 overflow-y-auto max-h-[230px] pr-1">
        {rows.length === 0 && <div className="text-sm text-slate-500 py-8 text-center" data-testid="mb-today-empty">Nessun servizio in questa data</div>}
        {rows.map((b) => (
          <button key={b.id} type="button" onClick={() => nav(`/prenotazioni?open=${b.id}`)} data-testid={`mb-today-row-${b.id}`}
            className="w-full grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_auto_1fr_auto_auto] items-center gap-3 px-3 py-2 rounded-lg border-l-2 border-[#D4A72C]/60 bg-white/[0.02] hover:bg-[#D4A72C]/[0.06] text-left transition-colors">
            <span className="font-mono-num text-[12px] text-white hidden sm:block">{b.booking_number}</span>
            <span className="flex items-center gap-1 font-mono-num text-[13px] text-[#F6E3A8]"><Clock size={13} />{b.time || "--:--"}</span>
            <span className="text-[12px] text-slate-300 truncate">{b.service_type || `${b.pickup || ""} → ${b.destination || ""}`}<span className="sm:hidden block text-[10px] text-slate-500 font-mono-num">{b.booking_number}</span></span>
            <span className="hidden sm:flex items-center gap-1 text-[12px] text-slate-400 truncate max-w-[140px]"><Car size={13} className="text-[#E9C46A]" />{vehicleName(b.vehicle_id) || "—"}</span>
            <span className={`text-[10.5px] px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_TONE[b.status] || STATUS_TONE.Preventivo}`}>{b.status}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

const Tile = ({ to, title, value, sub, img, testid }) => {
  const nav = useNavigate();
  return (
    <button type="button" onClick={() => nav(to)} data-testid={testid} className="mb-card mb-lift relative overflow-hidden h-[130px] md:h-[150px] p-4 text-left w-full">
      <img src={img} alt="" className="absolute right-0 bottom-0 h-full w-[62%] object-cover object-center opacity-90 [mask-image:linear-gradient(to_right,transparent,black_35%)]" />
      <span className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent sm:from-black/60 sm:via-transparent" />
      <span className="relative z-10 flex items-center justify-between text-white font-semibold text-base pr-1">{title}<ChevronRight size={18} className="text-[#E9C46A]" /></span>
      <span className="relative z-10 block mt-5 text-2xl md:text-[28px] font-semibold text-white font-mono-num" data-testid={`${testid}-value`}>{value}</span>
      <span className="relative z-10 block text-[12px] text-slate-400">{sub}</span>
    </button>
  );
};

export function QuickTiles({ vehicles, drivers, incToday }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mb-quick-tiles">
      <Tile to="/flotta" title="Flotta" value={vehicles} sub="Veicoli" img="/img/tiles/flotta.jpg" testid="mb-tile-flotta" />
      <Tile to="/autisti" title="Autisti" value={drivers} sub="In anagrafica" img="/img/tiles/autisti.jpg" testid="mb-tile-autisti" />
      <Tile to="/incassi" title="Incassi" value={fmtEUR(incToday)} sub="Incassato oggi" img="/img/tiles/incassi.jpg" testid="mb-tile-incassi" />
      <Tile to="/conteggi" title="Conteggi" value="" sub="Gestione mensile" img="/img/tiles/conteggi.jpg" testid="mb-tile-conteggi" />
    </div>
  );
}
